import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { IoChevronDownSharp, IoClose } from 'react-icons/io5';
import styled from 'styled-components';
import { Palette } from '../utils';
import { IconWithBg } from './IconWithBg';
import { resolveIconSrc } from './resolveIconSrc';
import {
  DropdownPanel,
  IconPickerTrigger,
  PickerRoot,
  TileButton,
  TileGrid,
  TileIconArea,
  TileLabel,
  TriggerTextInput
} from './iconSelectorStyles';

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 4px;
  color: ${Palette.CONTENT_TERTIARY};
  transition: all 0.15s ease;

  &:hover {
    background: ${Palette.BLACK_10};
    color: ${Palette.BLACK_50};
  }

  &:active {
    background: ${Palette.BLACK_20};
  }
`;

const IconSelector = forwardRef(function IconSelector(
  { options, value, onChange, onBlur, placeholder, hasError, disabled, id, name },
  ref
) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = useMemo(() => {
    if (value === null || value === undefined || value === '') return null;
    return (
      options.find((o) => o.value === value || String(o.value) === String(value)) || null
    );
  }, [options, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        if (onBlur) onBlur();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, onBlur]);

  const toggleOpen = () => {
    if (!disabled) setOpen((o) => !o);
  };

  const onTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleOpen();
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    if (onBlur) onBlur();
  };

  const listboxId = `${id || 'icon-selector'}-listbox`;

  return (
    <PickerRoot ref={rootRef}>
      <IconPickerTrigger
        $disabled={disabled}
        $hasError={hasError}
        aria-disabled={disabled || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={toggleOpen}
        onKeyDown={onTriggerKeyDown}>
        <TriggerTextInput
          ref={ref}
          type="text"
          id={id}
          name={name}
          readOnly
          disabled={disabled}
          tabIndex={-1}
          value={selected?.label ?? ''}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        {selected && !disabled && (
          <ClearButton
            type="button"
            onClick={handleClear}
            aria-label="Clear selection"
            tabIndex={-1}>
            <IoClose size={18} />
          </ClearButton>
        )}
        <IoChevronDownSharp size={18} color={Palette.CONTENT_TERTIARY} aria-hidden />
      </IconPickerTrigger>

      {open && (
        <DropdownPanel id={listboxId} role="listbox" aria-label={placeholder}>
          <TileGrid>
            {options.map((opt) => {
              const iconSrc = resolveIconSrc(opt.icon);
              const isSelected =
                selected != null &&
                (selected.value === opt.value || String(selected.value) === String(opt.value));
              return (
                <TileButton
                  key={String(opt.value)}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    if (onBlur) onBlur();
                  }}>
                  <TileIconArea>
                    <IconWithBg iconSrc={iconSrc} />
                  </TileIconArea>
                  <TileLabel $selected={isSelected}>{opt.label}</TileLabel>
                </TileButton>
              );
            })}
          </TileGrid>
        </DropdownPanel>
      )}
    </PickerRoot>
  );
});

export default IconSelector;
